<template>
  <div class="result-root relative w-full flex flex-col">
    <div class="flex-1 relative" :style="containerStyle">
      <model-viewer
        v-if="objUrl"
        bg-white
        class="h-full"
        :obj-url="objUrl"
        :shader-type="shaderType"
        :custom-shader="customShader"
        :measurement="currentMeasurement"
      />
      <template v-if="!isHM">
        <unit-switcher
          v-show="hasMeasurements"
          class="absolute top-0 left-0 right-0 mx-auto mt-10"
          :use-inch="useInch"
          :layout="measurementLayout"
          @toggle="onUnitSwitch"
        />
        <!-- Left -->
        <measurement-bar
          v-if="leftMeasurements[0]"
          :measurements="leftMeasurements"
          :active-index="activeMeasurementIndex"
          :use-inch="useInch"
          :layout="measurementLayout"
          @click="onMeasurementClick"
        />
        <!-- Right -->
        <measurement-bar
          v-if="rightMeasurements[0]"
          right
          :measurements="rightMeasurements"
          :active-index="activeMeasurementIndex - 5"
          :use-inch="useInch"
          :layout="measurementLayout"
          @click="onMeasurementClick($event + 5)"
        />
      </template>
    </div>
    <panel v-if="!measurementFailed" :height="panelHeight">
      <div class="w-full h-full text-center purple-bg p-10 flex-centered">
        <p
          class="scan-result-message"
          v-html="nothingYet ? $t('result.analyzing') : $t('result.scan_successful')"
        ></p>
      </div>
    </panel>
    <rounded-card v-else no-head :height="panelHeight">
      <error />
      <div v-if="showDoll" class="mini-doll flex flex-col my-5 justify-center">
        <p class="raise-hint" v-html="$t('result.raise')"></p>
        <doll
          :left="left"
          :right="right"
          :left-range="leftRange"
          :right-range="rightRange"
          mini
          left-breathing
          right-breathing
          @left-click="handleLeftClick"
          @right-click="handleRightClick"
          @left-hit="handleLeftHit"
          @right-hit="handleRightHit"
        />
      </div>
      <div v-else-if="!isContactless" class="w-full flex mt-auto mb-5 justify-around">
        <c-button width="40%" secondary class="my-3" @click="leave">
          <span>{{ $t('result.leave') }}</span>
        </c-button>
        <c-button v-if="!isMobileScan" width="40%" class="my-3" @click="retry">
          <span>{{ $t('result.scan_again') }}</span>
        </c-button>
      </div>
    </rounded-card>
    <leave-button v-if="canLeave" @click="leave" />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import audioPlayer from '@/mixins/audio-player';
import scanResults from '@/mixins/scan-results';

import ScanProcess from '@/modules/scan-process';

import MeasurementBar from '@/components/ShowResult/MeasurementBar.vue';
import ModelViewer from '@/components/common/ModelViewer.vue';
import Panel from '@/components/common/Panel.vue';
import UnitSwitcher from '@/components/common/UnitSwitcher.vue';
import Doll from '@/components/common/Doll.vue';
import Error from '@/components/ShowResult/Error.vue';
import LeaveButton from '@/components/common/LeaveButton.vue';
import RoundedCard from '@/components/common/RoundedCard.vue';
import CButton from '@/components/common/CButton.vue';

export default {
  name: 'ShowResult',
  components: {
    LeaveButton,
    MeasurementBar,
    Panel,
    ModelViewer,
    RoundedCard,
    Error,
    Doll,
    UnitSwitcher,
    CButton,
  },
  mixins: [audioPlayer, scanResults],
  mounted() {
    this.playAudio('16-Almost_there');
  },
  computed: {
    ...mapState('mtmUser', {
      mtmShaderType: state => state.shaderType,
      customShader: 'customShader',
    }),
    ...mapState('user', {
      userShaderType: state => state.shaderType,
    }),
    ...mapGetters('mtmUser', {
      hasMtmUserLoggedIn: 'isAuthenticated',
    }),
    shaderType() {
      if (this.hasMtmUserLoggedIn) return this.mtmShaderType;

      return this.userShaderType;
    },
    measurementLayout() {
      return (!this.isContactless && 'white') || '';
    },
    canLeave() {
      return !this.nothingYet && !this.isContactless && !this.measurementFailed;
    },
    isMobileScan() {
      return ScanProcess.type === 'mobile';
    },
  },
  watch: {
    hasMeasurements(nv) {
      if (nv) {
        this.playAudio('17-3D_Scan_Successful', true);
      }
    },
    measurementFailed(nv, ov) {
      if (nv && !ov && this.isContactless) {
        this.playAudio('18-3D_scan_unsuccessful', true);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.result-root {
  background-color: #fff;
}
.purple-bg {
  background-image: linear-gradient(to right, #2245FF, #540DEB);
  color: #fff;
  .scan-result-message {
    color: #fff;
  }
}
.scan-result-message {
  @apply text-xl font-bold;
  @screen md {
    @apply text-3xl;
    line-height: 60px;
  }
  line-height: 1.67;
}
.mini-doll {
  overflow: hidden;
}
.raise-hint {
  @apply text-2xl text-center px-2 mx-auto my-3;
  color: #111;
}
</style>
